/* eslint-disable react-refresh/only-export-components */
import { StandardIcon } from '@/components/common/icon';
import { MinistryRoleAssignees } from '@/components/common/output/ministry';
import type { Column } from '@/components/common/table';
import type { Ministry } from '@/shared/types/ministry';
import {
  getMinistryTypeIcon,
  getMinistryTypeName,
  getOrgRoleLabel,
  type MinistryRoleKey,
} from '@/store/org/types';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Switch,
} from '@heroui/react';
import { Icon } from '@iconify/react/dist/iconify.js';

export function makeColumns(
  onAction: (item: Ministry, key: ActionKey) => void,
  onActiveChange: (item: Ministry, value: boolean) => void,
): MinistryColumn[] {
  return [
    {
      key: 'name',
      label: 'Name',
      display: MinistryName,
    },
    {
      key: 'active',
      label: 'Active',
      display: ({ item }: { item: Ministry; column: Column<Ministry> }) => (
        <Switch
          isSelected={item.data.active}
          onValueChange={(value: boolean) => onActiveChange(item, value)}
          size='sm'
        />
      ),
    },
    {
      key: 'oversight',
      label: getOrgRoleLabel('ministry', 'oversight', true),
      display: (props: { item: Ministry; column: Column<Ministry> }) =>
        MinistryRoleAssigneesDisplay({ role: 'oversight', ...props }),
    },
    {
      key: 'directors',
      label: getOrgRoleLabel('ministry', 'director', true),
      display: (props: { item: Ministry; column: Column<Ministry> }) =>
        MinistryRoleAssigneesDisplay({ role: 'director', ...props }),
    },
    {
      key: 'actions',
      label: '',
      display: ({
        item,
        column,
      }: {
        item: Ministry;
        column: Column<Ministry>;
      }) => <MinistryActions item={item} column={column} onAction={onAction} />,
    },
  ];
}

export type ColumnKey =
  | 'name'
  | 'active'
  | 'oversight'
  | 'directors'
  | 'admins'
  | 'actions';
export type ActionKey = 'edit';

export interface MinistryColumn extends Column<Ministry> {
  key: ColumnKey;
}

function MinistryName({ item }: { item: Ministry; column: Column<Ministry> }) {
  return (
    <div className='flex flex-row items-center'>
      <StandardIcon id={getMinistryTypeIcon(item.data.type)} className='mr-1' />
      {getMinistryTypeName(item.data.type)}
    </div>
  );
}

// Active cell is now rendered inline in makeColumns to capture onActiveChange

function MinistryRoleAssigneesDisplay({
  item,
  role,
}: {
  item: Ministry;
  role: MinistryRoleKey;
  column: Column<Ministry>;
}) {
  return <MinistryRoleAssignees item={item} role={role} />;
}

function MinistryActions({
  item,
  onAction,
}: {
  item: Ministry;
  column: Column<Ministry>;
  onAction: (item: Ministry, key: ActionKey) => void;
}) {
  return (
    <Dropdown>
      <DropdownTrigger>
        <Button isIconOnly variant='light'>
          <Icon
            icon='mdi:dots-vertical'
            width={22}
            height={22}
            className='text-gray-600'
          />
        </Button>
      </DropdownTrigger>
      <DropdownMenu>
        <DropdownItem key='edit' onPress={() => onAction(item, 'edit')}>
          Edit
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
}
